/**
 * FamilyDevices.jsx — Family device manager (family tier).
 *
 * Lists LAN devices flagged as family: approve pending ones, revoke,
 * rename (label), and keep shared notes. Talks to /api/v2/family/*.
 */
import { createSignal, For, Show } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import toast from '../lib/toastStore.js';

async function familyPost(path, body, method = 'POST') {
  const res = await fetch(`/api/v2/family${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `Request failed (${res.status})`);
  return json;
}

const STATUS_META = {
  pending:  { label: 'pending',  color: '#d4a24e' },
  approved: { label: 'approved', color: '#4ade80' },
  revoked:  { label: 'revoked',  color: '#ff4d2d' },
};

function fmtSeen(ts) {
  if (!ts) return '—';
  const mins = Math.round((Date.now() - ts) / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}

export default function FamilyDevices() {
  const { data, loading, error, refetch } = useWidgetData('family-devices', () => ({}));
  const [busy, setBusy] = createSignal(null); // device id in flight
  const [editing, setEditing] = createSignal(null); // { id, label, notes }

  async function act(id, action) {
    setBusy(id);
    try {
      await familyPost(`/devices/${id}/${action}`);
      toast.success(`Device ${action}d.`);
      refetch();
    } catch (err) {
      toast.error(err.message);
    } finally { setBusy(null); }
  }

  async function saveEdit() {
    const e = editing();
    if (!e) return;
    setBusy(e.id);
    try {
      await familyPost(`/devices/${e.id}`, { label: e.label, notes: e.notes }, 'PATCH');
      toast.success('Device updated.');
      setEditing(null);
      refetch();
    } catch (err) {
      toast.error(err.message);
    } finally { setBusy(null); }
  }

  async function updateFamilySettings(body, message) {
    setBusy('enrollment');
    try {
      const result = await familyPost('/settings', body, 'PATCH');
      toast.success(message(result));
      refetch();
    } catch (err) {
      toast.error(err.message);
    } finally { setBusy(null); }
  }

  return (
    <div class="v2-widget v2-family-devices">
      <div class="v2-widget-hdr">
        <span class="v2-widget-hdr__title">🏠 Family Devices</span>
        <Show when={data()?.pendingCount > 0}>
          <span style={{
            'margin-left': 'auto', padding: '1px 8px', 'border-radius': '999px',
            'font-size': '11px', background: 'rgba(212,162,78,.15)', color: '#d4a24e',
          }}>
            {data().pendingCount} pending
          </span>
        </Show>
      </div>
      <Show when={data()?.canManageSettings}>
        <div style={{ padding: 'var(--sp-2)', 'border-bottom': '1px solid var(--border, rgba(255,255,255,.08))' }}>
          <div style={{ 'font-size': '12px', 'margin-bottom': '6px' }}>
            New device enrollment: <strong>{data()?.enrollmentEnabled ? 'enabled' : 'disabled'}</strong>
          </div>
          <button class="v2-home__btn" disabled={busy() === 'enrollment'}
            onClick={() => updateFamilySettings(
              { enrollmentEnabled: !data()?.enrollmentEnabled },
              result => result.enrollmentEnabled ? 'New device enrollment enabled.' : 'New device enrollment disabled.',
            )}>
            {data()?.enrollmentEnabled ? 'Disable enrollment' : 'Enable enrollment'}
          </button>
          <div style={{ 'font-size': '12px', 'margin-top': '10px', 'margin-bottom': '6px' }}>
            Family members may manage devices: <strong>{data()?.familyManagementEnabled ? 'enabled' : 'owner only'}</strong>
          </div>
          <button class="v2-home__btn" disabled={busy() === 'enrollment'}
            onClick={() => updateFamilySettings(
              { familyManagementEnabled: !data()?.familyManagementEnabled },
              result => result.familyManagementEnabled ? 'Family device management enabled for family members.' : 'Family device management is owner-only.',
            )}>
            {data()?.familyManagementEnabled ? 'Require owner for device management' : 'Allow family device management'}
          </button>
          <div style={{ 'font-size': '12px', 'margin-top': '10px', 'margin-bottom': '6px' }}>
            Join-family prompt: <strong>{data()?.joinFamilyVisible ? 'visible' : 'hidden'}</strong>
          </div>
          <button class="v2-home__btn" disabled={busy() === 'enrollment'}
            onClick={() => updateFamilySettings(
              { joinFamilyVisible: !data()?.joinFamilyVisible },
              result => result.joinFamilyVisible ? 'Join-family prompt shown on LAN devices.' : 'Join-family prompt hidden.',
            )}>
            {data()?.joinFamilyVisible ? 'Hide join-family prompt' : 'Show join-family prompt'}
          </button>
        </div>
      </Show>
      <Show when={error()} fallback={
        <Show when={!loading()} fallback={<div class="v2-widget-skel">Loading…</div>}>
          <div class="v2-widget__scroll" style={{ padding: 'var(--sp-2)' }}>
            <Show when={data()?.devices?.length} fallback={
              <p style={{ opacity: 0.7, padding: 'var(--sp-2)' }}>
                No devices yet. Family members can flag their device from the
                home menu while on the house network.
              </p>
            }>
              <For each={data().devices}>
                {(d) => (
                  <div style={{
                    border: '1px solid var(--border, rgba(255,255,255,.08))',
                    'border-radius': '8px', padding: 'var(--sp-2)',
                    'margin-bottom': 'var(--sp-2)',
                  }}>
                    <div style={{ display: 'flex', 'align-items': 'center', gap: '8px' }}>
                      <strong>{d.label || 'Household device'}</strong>
                      <span style={{
                        padding: '0 8px', 'border-radius': '999px', 'font-size': '11px',
                        background: `${STATUS_META[d.status]?.color || '#888'}22`,
                        color: STATUS_META[d.status]?.color || '#888',
                      }}>{STATUS_META[d.status]?.label || d.status}</span>
                      <span style={{ 'margin-left': 'auto', 'font-size': '11px', opacity: 0.6 }}>
                        seen {fmtSeen(d.lastSeen)}
                      </span>
                    </div>
                    <Show when={d.mac || d.ip}>
                      <div style={{ 'font-size': '12px', opacity: 0.7, 'margin-top': '2px' }}>
                        {d.mac || 'device details'} · {d.ip || 'no recent IP'}
                      </div>
                    </Show>
                    <Show when={d.notes && editing()?.id !== d.id}>
                      <div style={{ 'font-size': '12px', 'margin-top': '4px', 'white-space': 'pre-wrap' }}>{d.notes}</div>
                    </Show>
                    <Show when={editing()?.id === d.id}>
                      <div style={{ 'margin-top': 'var(--sp-2)', display: 'grid', gap: '6px' }}>
                        <input
                          value={editing().label || ''}
                          placeholder="Label (e.g. Mom's phone)"
                          onInput={(e) => setEditing({ ...editing(), label: e.currentTarget.value })}
                        />
                        <textarea
                          rows="2"
                          value={editing().notes || ''}
                          placeholder="Notes for the family…"
                          onInput={(e) => setEditing({ ...editing(), notes: e.currentTarget.value })}
                        />
                      </div>
                    </Show>
                    <div style={{ display: 'flex', gap: '6px', 'margin-top': 'var(--sp-2)', 'flex-wrap': 'wrap' }}>
                      <Show when={data()?.canManage && editing()?.id === d.id} fallback={
                        <>
                          <Show when={data()?.canManage && d.status === 'pending'}>
                            <button class="v2-home__btn v2-home__btn--primary" disabled={busy() === d.id}
                              onClick={() => act(d.id, 'approve')}>Approve</button>
                          </Show>
                          <Show when={data()?.canManage && d.status === 'approved'}>
                            <button class="v2-home__btn" disabled={busy() === d.id}
                              onClick={() => act(d.id, 'revoke')}>Revoke</button>
                          </Show>
                          <Show when={data()?.canManage && d.status === 'revoked'}>
                            <button class="v2-home__btn" disabled={busy() === d.id}
                              onClick={() => act(d.id, 'approve')}>Re-approve</button>
                          </Show>
                          <Show when={data()?.canEdit}>
                            <button class="v2-home__btn" disabled={busy() === d.id}
                            onClick={() => setEditing({ id: d.id, label: d.label, notes: d.notes })}>Edit</button>
                          </Show>
                        </>
                      }>
                        <button class="v2-home__btn v2-home__btn--primary" disabled={busy() === d.id}
                          onClick={saveEdit}>Save</button>
                        <button class="v2-home__btn" onClick={() => setEditing(null)}>Cancel</button>
                      </Show>
                    </div>
                  </div>
                )}
              </For>
            </Show>
          </div>
        </Show>
      }>
        <div class="v2-error">{error()}</div>
      </Show>
    </div>
  );
}
